import { Link, Stack, Typography } from '@mui/material';
import React, { useTransition } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { TLegalPage } from '@/reseller/reseller.types';
import { Alert } from '@/shared/components/ui/alert/alert';
import { Button } from '@/shared/components/ui/button/button';
import {
  Modal,
  ModalContainer,
} from '@/shared/components/ui/modal-controller/modal-controller.styled';
import { LanguageSwitcher } from '@/shared/features/language-switcher/language-switcher';
import { ThemeSwitcher } from '@/shared/features/theme-switcher/components/theme-switcher';
import { useTermsAndConditions } from '@/shared/hooks/use-terms-and-conditions/use-terms-and-conditions';
import { logout } from '@/shared/redux/api/auth/auth-api';
import { useHandleTermsAndConditionsAcceptedForBillingMutation } from '@/shared/redux/api/billing/billing-api';
import { useAcceptTermsAndConditionsMutation } from '@/shared/redux/api/ident/ident-api';
import { useAppDispatch } from '@/shared/redux/hooks/hooks';
import { getReseller } from '@/shared/utils/reseller/reseller';
import { showToastError } from '@/shared/utils/toast';

// Eagerly import all reseller legal pages
const resellerLegalPages = import.meta.glob<{
  LEGAL_PAGES: Record<TLegalPage, string>;
}>('../../../../../reseller/*/external-pages/legal-pages.constants.ts', {
  eager: true,
});

// Get the reseller legal pages based on environment variable
const getResellerLegalConstants = (): {
  pages: Record<TLegalPage, string> | null;
  reseller: string;
  publicRootPath: string;
} | null => {
  const reseller = getReseller();
  const publicRootPath =
    import.meta.env.VITE_APP_RESELLER_PUBLIC_ROOT_PATH ?? '';

  if (!reseller) {
    return null;
  }

  // Find the legal pages that matches the reseller name
  const legalPagesEntry = Object.entries(resellerLegalPages).find(([path]) =>
    path.includes(`/${reseller}/external-pages/legal-pages.constants.ts`),
  );

  if (!legalPagesEntry) {
    return null;
  }

  return {
    pages: legalPagesEntry[1].LEGAL_PAGES,
    reseller,
    publicRootPath,
  };
};

export const AcceptPrivacyPolicy = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isLatestAccepted, isNewVersionAvailable, isLoading } =
    useTermsAndConditions();
  const [isPending, startTransition] = useTransition();
  const navigate = useNavigate();
  const [handleAcceptedForBilling] =
    useHandleTermsAndConditionsAcceptedForBillingMutation();
  const [acceptTermsAndConditions] = useAcceptTermsAndConditionsMutation();
  const legalConstants = getResellerLegalConstants();
  const reseller = legalConstants?.reseller ?? 'sda';
  const publicRootPath = legalConstants?.publicRootPath ?? '';
  const pages = legalConstants?.pages ?? [];

  const handleAcceptPrivacyPolicy = async () => {
    startTransition(async () => {
      try {
        await acceptTermsAndConditions().unwrap();
        await handleAcceptedForBilling().unwrap();
        toast.success(t('onboarding:privacy_policy_accepted'));
      } catch {
        showToastError(t('error:something_went_wrong'));
      }
    });
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate(0);
  };

  const transformUrl = (url: string) => {
    if (url.startsWith('/')) {
      return `${publicRootPath}${url}`;
    }
    return url;
  };

  const renderDoubleRowBreak = () => (
    <React.Fragment key="double-row-break">
      <br />
      <br />
    </React.Fragment>
  );

  if (isLoading || isLatestAccepted || !legalConstants) {
    return null;
  }

  return (
    <>
      <Stack
        direction="row"
        gap="1rem"
        position="fixed"
        top="1rem"
        right="1rem"
        zIndex={99999}
      >
        <ThemeSwitcher />
        <LanguageSwitcher />
      </Stack>
      <Modal open={true} disableEscapeKeyDown>
        <ModalContainer width="30rem">
          <Stack gap="2rem">
            <Stack gap="1.25rem">
              <Typography variant="h5" textAlign="center">
                {t('onboarding:accept_privacy_policy_title')}
              </Typography>
              {isNewVersionAvailable && (
                <Alert
                  color="blue"
                  text={t('onboarding:terms_new_version_required')}
                />
              )}
              <Typography variant="p5" textAlign="center" color="textSecondary">
                <Trans
                  i18nKey={`${reseller}.accept_terms`}
                  ns="reseller"
                  components={[
                    ...Object.entries(pages).map(([page, url]) => (
                      <Link
                        href={transformUrl(url)}
                        key={page}
                        target="_blank"
                      />
                    )),
                    renderDoubleRowBreak(),
                  ]}
                />
              </Typography>
            </Stack>
            <Stack direction="row" gap="0.75rem" width="100%">
              <Button
                variant="secondary"
                fullWidth
                size="large"
                disabled={isPending}
                onClick={handleLogout}
              >
                {t('common:decline')}
              </Button>
              <Button
                fullWidth
                size="large"
                loading={isPending}
                onClick={handleAcceptPrivacyPolicy}
              >
                {t('common:accept')}
              </Button>
            </Stack>
          </Stack>
        </ModalContainer>
      </Modal>
    </>
  );
};
